'use client'

import { useEffect, useState, useCallback, useRef } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Globe, ChevronDown } from 'lucide-react'
import { cn } from '@/utilities/ui'
import { trackEvent } from '@/utilities/analytics/trackEvent'

declare global {
  interface Window {
    Localize?: {
      setLanguage: (lang: string) => void
      getLanguage: () => string
      on: (event: string, callback: (data?: { language?: string }) => void) => void
      off: (event: string, callback: (data?: { language?: string }) => void) => void
    }
    zE?: (widget: string, action: string, ...args: unknown[]) => void
  }
}

const languages = [
  { code: 'en', label: 'English' },
  { code: 'es-US', label: 'Spanish' },
] as const

const POLL_INTERVAL_MS = 100
const MAX_POLL_ATTEMPTS = 50

function shortLabel(code: string) {
  const base = code.replace(/-.*/, '')
  return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase()
}

export function LanguageDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState<string>('en')
  const [localizeReady, setLocalizeReady] = useState(false)
  const pollCountRef = useRef(0)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const isLocalizeReady = useCallback(() => {
    if (window.Localize?.getLanguage) {
      const lang = window.Localize.getLanguage()
      return typeof lang === 'string' && lang.length > 0
    }
    return false
  }, [])

  const syncLanguage = useCallback(() => {
    if (window.Localize?.getLanguage) {
      const lang = window.Localize.getLanguage()
      if (lang && lang.length > 0) {
        setCurrentLanguage(lang)
        return true
      }
    }
    return false
  }, [])

  useEffect(() => {
    if (localizeReady) return

    if (isLocalizeReady()) {
      setLocalizeReady(true)
      return
    }

    const pollInterval = setInterval(() => {
      pollCountRef.current += 1

      if (isLocalizeReady()) {
        setLocalizeReady(true)
        clearInterval(pollInterval)
      } else if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
        clearInterval(pollInterval)
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(pollInterval)
  }, [localizeReady, isLocalizeReady])

  useEffect(() => {
    if (!localizeReady) return

    syncLanguage()

    const handleChange = (data?: { language?: string }) => {
      if (data?.language) {
        setCurrentLanguage(data.language)
      } else {
        syncLanguage()
      }
    }

    window.Localize?.on?.('initialize', syncLanguage)
    window.Localize?.on?.('setLanguage', handleChange)

    return () => {
      window.Localize?.off?.('initialize', syncLanguage)
      window.Localize?.off?.('setLanguage', handleChange)
    }
  }, [localizeReady, syncLanguage])

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    trackEvent('navigation:language_menu_toggle', { state: open ? 'open' : 'close' })
  }

  const handleLanguageChange = (langCode: string) => {
    if (!window.Localize?.setLanguage) return
    if (langCode !== currentLanguage) {
      trackEvent('navigation:language_change', { from: currentLanguage, to: langCode })
    }
    window.Localize.setLanguage(langCode)
    if (typeof window.zE === 'function') {
      window.zE('messenger:set', 'locale', langCode)
    }
    setCurrentLanguage(langCode)
    setIsOpen(false)
  }

  return (
    <PopoverPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label="Select language"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          data-testid="language-dropdown-button"
          className={cn(
            'flex items-center gap-1 cursor-pointer text-tenant-primary-action-color',
            'hover:opacity-80 transition-opacity',
          )}
        >
          <Globe className="size-4 shrink-0 text-tenant-description-text" aria-hidden="true" />
          <span className="text-base">{shortLabel(currentLanguage)}</span>
          <ChevronDown
            className={cn(
              'size-6 shrink-0 transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="end"
          sideOffset={8}
          className={cn(
            'z-50 w-40 rounded-3xl border border-tenant-border bg-tenant-card-background text-tenant-card-foreground p-2 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            const target = event.detail.originalEvent.target
            if (target instanceof Node && triggerRef.current?.contains(target)) {
              event.preventDefault()
            }
          }}
          data-testid="language-dropdown"
          role="listbox"
          aria-label="Language options"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={currentLanguage === lang.code}
              data-testid={`language-dropdown-option-${lang.code}`}
              onClick={() => handleLanguageChange(lang.code)}
              className={cn(
                'flex w-full cursor-pointer items-center gap-3 rounded-full px-3 py-3',
                'text-sm hover:bg-tenant-subtle-accent-background',
                currentLanguage === lang.code && 'font-medium underline',
              )}
            >
              {lang.label}
            </button>
          ))}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
